import { System, Entity, Not } from "ecsy";
import { Physics, Ownership, Chainer, Antibody, BloodCell, Transform, Moved, Chained, PreviewOnly, Dead } from "../components";
import { queryRadius, makeWeldJoint, severWeldJoint } from "../box2dUtils";
import { getOrAddComponent, getSystem } from "../utils";
import { PhysicsSystem } from "./physicsSystem";
import { Game } from "../game";
import { getEntityById, getId } from "../entityFactory";
import { Chaining } from "../constants";
import { vec2 } from "@tlaukkan/tsm";

const byId = (a: Entity, b: Entity) => (getId(a) || 0) - (getId(b) || 0); //used to ensure determininstic behavior between client and server when range query order can vary

export class ChainSystem extends System {
	private physicsSystem!: PhysicsSystem
	private game: Game;

	persistedLinks = new Map<number, Array<number>>();
	chainsActive = false;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	init() {
		this.physicsSystem = getSystem(this.world, PhysicsSystem);
	}

	execute() {
		//Merge a players blood cells at the start of every round
		const cells = this.queries.bloodCells.added || [];
		const playerIds = this.game.players.map(player => player.id);
		for (const id of playerIds) {
			const playerCells = cells.filter(cell =>
				cell.hasComponent(Ownership) &&
				cell.getMutableComponent(Ownership).playerId === id
			);
			if (playerCells.length > 0) {
				console.log('merging bodies');
				this.physicsSystem.mergeBodies(playerCells);
			}
		}

		//Restore connections when entities are recreated.
		this.queries.chainer.added?.forEach(entity => {
			const id = getId(entity) || -1;
			const links = this.persistedLinks.get(id);
			if (!links) return;

			links.map(id => getEntityById(id)!)
				.filter(entity => entity !== null)
				.sort((a, b) => (getId(a) || 0) - (getId(b) || 0))
				.forEach(link => {
					const connectionAdded = this.addConnection(entity, link);
					const chainer = entity.getMutableComponent(Chainer);
					if (chainer) chainer.lastStepLinks = chainer.links; //otherwise this won't get set in sim state
					if (connectionAdded && this.chainsActive) {
						console.log(`welding connection between ${getId(entity)} and ${getId(link)}`)
						this.weld(entity, link);
					}
				});
		});

		//Client should not be forming links and might delete the links from the server, so only run this clear on the server
		if (process.env.is_server && !this.chainsActive && this.persistedLinks.size > 0) {
			this.persistedLinks.clear();
		}

		if (!this.chainsActive) {
			//Reset the chained things each frame
			this.queries.chained.results.forEach(entity => {
				const chained = entity.getComponent(Chained);
				if (chained && chained.links.length > 0) {
					chained.links.length = 0; //clear links
				}
			});

			//Chainer queries nearby cells and adds nearest to connections.
			[...this.queries.chainer.results]
				.sort(byId)
				.forEach(entity => {
					this.updateChainedEntities(entity);
				});
		}
		else {
			//Physics chains break automatically but link and source lists need to be updated on death
			if (this.queries.chained.removed) {
				const removeNext = new Array<Entity>();
				const toBeRemoved = new Set(this.queries.chained.removed)
				this.queries.chained.removed.forEach(entity => {
					const chained = entity.getComponent(Chained) || entity.getRemovedComponent(Chained);
					chained.links.forEach(source => {
						const sourceChainer = source.getComponent(Chained);
						if (sourceChainer) {
							const indexOfEntity = sourceChainer.links.indexOf(entity);
							if (indexOfEntity === -1) return; //entity not found in sourceChainer.links
							sourceChainer.links.splice(indexOfEntity, 1);
							if (sourceChainer.links.length === 0 && !toBeRemoved.has(source)) {
								removeNext.push(source);
								toBeRemoved.add(source);
							}
						}
						//Note: In theory we should check for Chainer and reduce the links count, but we don't need to because 
						//the during simulation the links count doesn't need to be up to date, and during shop it regenerates each frame anyway.
					});

				});
				for (const emptyChains of removeNext) {
					emptyChains.removeComponent(Chained);
				}
			}
		}
	}

	lockConnections() {
		console.log('locking connections');
		[...this.queries.chained.results]
			.sort(byId) //make sure order of merging in well-defined
			.forEach(entity => {
				const chained = entity.getComponent(Chained);
				chained.links.forEach(link => {
					this.persistConnection(entity, link);
				});
			});
	}

	private updateChainedEntities(entity: Entity) {
		const chainer = entity.getComponent(Chainer);
		const transform = entity.getComponent(Transform);

		chainer.lastStepLinks = chainer.links;
		chainer.links = 0;

		const chainables = this.getNearbyChainables(entity, Chaining.ChainRange);
		const orderedByDistance = chainables
			.map(entity => {
				const targetTransform = entity.getComponent(Transform);
				return [entity, vec2.squaredDistance(transform.position, targetTransform.position)] as [Entity, number];
			})
			.sort((a: [Entity, number], b: [Entity, number]) => a[1] - b[1] || byId(a[0], b[0]))
			.map(pair => pair[0]);

		//Bond to the nearest cells, but at most one blood cell
		let bonds = 0;
		let hasBloodCellBond = false;
		for (const link of orderedByDistance) {
			if (bonds >= chainer.maxLinks) break;
			const isBloodCell = link.hasComponent(BloodCell);
			if (isBloodCell && hasBloodCellBond) continue;
			//Already connected means another chain cell bonded to us, which shouldn't cost us one of our own bonds
			if (!this.addConnection(entity, link)) continue;
			hasBloodCellBond = hasBloodCellBond || isBloodCell;
			++bonds;
		}
	}

	private getNearbyChainables(entity: Entity, range: number, filter: (hit: Entity) => boolean = () => true) {
		const owner = entity.getComponent(Ownership);
		const transform = entity.getComponent(Transform);

		return queryRadius(transform.position.copy(), range, (hit) =>
			filter(hit)
			&& hit.hasComponent(Ownership)
			&& hit.hasComponent(Physics)
			&& (hit.hasComponent(Antibody) || hit.hasComponent(BloodCell))
			&& !hit.hasComponent(Dead)
			&& hit !== entity
			&& hit.getComponent(Ownership).playerId === owner.playerId
		);
	}

	private addConnection(source: Entity, target: Entity) {
		const chainer = source.getMutableComponent(Chainer);
		if (!chainer) return false;

		//connect me to them
		const sourceChains = getOrAddComponent(source, Chained);
		if (sourceChains.links.includes(target)) return false;
		sourceChains.links.push(target);
		chainer.links += 1;

		//connect them to me (if not already connected)
		const targetChains = getOrAddComponent(target, Chained);
		if (!targetChains.links.includes(source)) {
			targetChains.links.push(source);
		}

		return true;
	}

	private persistConnection(source: Entity, target: Entity) {
		const sourceId = getId(source);
		const targetId = getId(target);

		if (sourceId == null || targetId == null || !source.hasComponent(Chainer)) return;

		// Intentionally allowing bidirectional links. Preserve this information, 
		//just stop double melding at the actual joint application stage.
		// const targetLinks = this.persistedLinks.get(targetId)
		// if (targetLinks && targetLinks.includes(sourceId)) {
		// 	return;
		// }

		//Add the link from source to target, if its not already there
		if (!this.persistedLinks.has(sourceId)) {
			this.persistedLinks.set(sourceId, []);
		}
		const linkList = this.persistedLinks.get(sourceId)!

		//If the link target (which is anything) is not in this add it to the source's group
		if (!linkList.find(link => link === targetId)) {
			linkList.push(targetId);
		}
	}


	private weld(entity: Entity, link: Entity) {
		const body = entity.getComponent(Physics).body;
		const linkBody = link.getComponent(Physics).body;
		return makeWeldJoint(body, linkBody);
	}
}

ChainSystem.queries = {
	bloodCells: { components: [BloodCell, Physics], listen: { added: true } },
	chainer: { components: [Chainer, Physics, Ownership], listen: { added: true, removed: true } },
	chained: { components: [Chained, Physics], listen: { removed: true, } }
}